import {ChangeDetectionStrategy, Component, computed, inject, signal, Signal} from '@angular/core';
import {FirebaseService} from '../../../shared/services/firebase.service';
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {LanguageService} from '../../../shared/services/language.service';
import {TYPE} from '../../../shared/types/types';
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DialogUtility} from '@syncfusion/ej2-angular-popups';

@Component({
  selector: 'app-browser-notifications',
  imports: [ContentComponent, FormButtonComponent, TranslatePipe],
  template: `
    <div class="d-flex flex-column my-2">
      <app-content
        [fontSize]="'fs-default'"
        [fontColor]="'text-muted'"
        >
        {{statusMessage()}}
      </app-content>
      @if(firebase.notificationState() === defaultType()){
        <app-form-button
          [icon]="'notification_important'" 
          [iconPosition]="'left'" 
          [cssClass]="'e-primary e-small fs-default fw-semibold'" 
          [iconClass]="'fs-5'"
          [text]="'REQUESTBROWSERASKNOTIFICATIONS' | translate"
          (click)="firebase.init(true)"
        >
        </app-form-button>
      }
      <div class="col-md-4">
        <app-form-button
          (click)="showNotificationsHelpDialog()"
          [text]="'FURTHERNOTIFICATIONHELP' | translate" 
          [cssClass]="'e-flat e-small fs-default fw-bold text-primary p-0'"
          [icon]="'help_outline'" 
          [iconPosition]="'left'" 
          [iconClass]="'fs-5'"
          >
        </app-form-button>
      </div>
    </div>
  `,
  styleUrl: './browser-notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrowserNotificationsComponent {
  public firebase: FirebaseService = inject(FirebaseService);
  private languageService: LanguageService = inject(LanguageService);

  public defaultType: Signal<TYPE.FirebaseNotificationsState> = signal(TYPE.FirebaseNotificationsState.DEFAULT);

  public statusMessage: Signal<string> = computed(() => {
    const message = this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUS') + ': ';
    switch(this.firebase.notificationState()) {
      case TYPE.FirebaseNotificationsState.INITIALIZING:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSINITIALIZING');
      case TYPE.FirebaseNotificationsState.UNDEFINED:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSUNKNOWNBROWSERSTATE');
      case TYPE.FirebaseNotificationsState.UNSUPPORTED:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSUNSUPPORTED');
      case TYPE.FirebaseNotificationsState.ERROR:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSINSTALLATIONERROR');
      case TYPE.FirebaseNotificationsState.GRANTED:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSGRANTED');
      case TYPE.FirebaseNotificationsState.DENIED:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSDENIED');
      case TYPE.FirebaseNotificationsState.DEFAULT:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSNOUSERACTION');
      default:
        return message + this.languageService.getTranslationByKey('BROWSERNOTIFICATIONSSTATUSUNKNOWN');
    }
  });

  public showNotificationsHelpDialog(): void {
    DialogUtility.alert({
      title: '<h6 class="fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTITLE') + '</h6>',
      content: `
        <div>${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXT')}</div>
        </br>
        </br>
        <div>${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTBROWSERS')}</div>
        <a href="https://support.google.com/chrome/answer/3220216" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTCHROME')}</a>
        </br>
        <a href="https://support.mozilla.org/de/kb/push-benachrichtigungen-firefox" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTFIREFOX')}</a>
        </br>
        <a href="https://support.microsoft.com/de-de/edge/manage-website-notifications-in-microsoft-edge" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTEDGE')}</a>
        </br>
        <a href="https://support.apple.com/de-de/guide/safari/sfri40734/mac" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTSAFARI')}</a>
        </br>
        </br>
        <div>${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTOPERATINGSYSTEMS')}</div>
        <a href="https://support.microsoft.com/de-de/windows/benachrichtigungen-und-nicht-st%C3%B6ren-in-windows-feeca47f-0baf-5680-16f0-8801db1a8466" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTWINDOWS')}</a>
        </br>
        <a href="https://support.apple.com/de-de/guide/mac-help/mchl205da693/mac" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTMACOS')}</a>
        </br>
        <a href="https://support.google.com/android/answer/9079661" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTANDROID')}</a>
        </br>
        <a href="https://support.apple.com/de-de/guide/iphone/iph7c3d96bab/ios" target="_blank" >${this.languageService.getTranslationByKey('NOTIFICATIONHELPDIALOGTEXTIPHONE')}</a>
        </br>
      `,
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }
}
